/**
 * examService.js
 * Exam and exam result Firestore operations
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  addDoc, deleteDoc, query, where, orderBy, onSnapshot,
  serverTimestamp, writeBatch, increment
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { COLLECTIONS } from './collections';

/**
 * Get all exams
 */
export async function getAllExams() {
  const snap = await getDocs(collection(db, COLLECTIONS.EXAMS));
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get exam by ID
 */
export async function getExamById(examId) {
  const snap = await getDoc(doc(db, COLLECTIONS.EXAMS, examId));
  return snap.exists() ? { key: snap.id, ...snap.data() } : null;
}

/**
 * Get exams by class
 */
export async function getExamsByClass(classId) {
  const q = query(
    collection(db, COLLECTIONS.EXAMS),
    where('classId', '==', classId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get exams by multiple classes
 */
export async function getExamsByClasses(classIds) {
  if (!classIds || classIds.length === 0) return [];
  const results = await Promise.all(
    classIds.map(id => getExamsByClass(id))
  );
  return results.flat();
}

/**
 * Create exam
 */
export async function createExam(data) {
  const docRef = await addDoc(collection(db, COLLECTIONS.EXAMS), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Update exam
 */
export async function updateExam(examId, data) {
  await updateDoc(doc(db, COLLECTIONS.EXAMS, examId), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete exam
 */
export async function deleteExam(examId) {
  await deleteDoc(doc(db, COLLECTIONS.EXAMS, examId));
}

/**
 * Subscribe to exam results (real-time) - useful for grading
 */
export function subscribeToExamResults(examId, callback) {
  const q = query(
    collection(db, COLLECTIONS.EXAM_RESULTS),
    where('examId', '==', examId)
  );
  return onSnapshot(q, (snap) => {
    const results = snap.docs.map(d => ({ key: d.id, ...d.data() }));
    callback(results);
  });
}

/**
 * Subscribe to exam results with raw snapshot access (for advanced use like violation tracking)
 */
export function subscribeToExamResultsRaw(examId, callback) {
  const q = query(
    collection(db, COLLECTIONS.EXAM_RESULTS),
    where('examId', '==', examId)
  );
  return onSnapshot(q, callback);
}

/**
 * Get exam results by exam
 */
export async function getExamResults(examId) {
  const q = query(
    collection(db, COLLECTIONS.EXAM_RESULTS),
    where('examId', '==', examId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Get exam results by student
 */
export async function getExamResultsByStudent(studentId) {
  const q = query(
    collection(db, COLLECTIONS.EXAM_RESULTS),
    where('studentId', '==', studentId)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ key: d.id, ...d.data() }));
}

/**
 * Submit exam result
 */
export async function submitExamResult(data) {
  const { examId, studentId, resultKey, score, submittedAt, answers } = data;

  // Check if result already exists
  if (resultKey) {
    await updateDoc(doc(db, COLLECTIONS.EXAM_RESULTS, resultKey), {
      score,
      answers,
      submittedAt: submittedAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return resultKey;
  }

  // Create new result
  const docRef = await addDoc(collection(db, COLLECTIONS.EXAM_RESULTS), {
    examId,
    studentId,
    score,
    answers,
    submittedAt: submittedAt || serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Grade exam result
 */
export async function gradeExamResult(resultId, gradeData) {
  await updateDoc(doc(db, COLLECTIONS.EXAM_RESULTS, resultId), {
    ...gradeData,
    gradedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete exam result
 */
export async function deleteExamResult(resultId) {
  await deleteDoc(doc(db, COLLECTIONS.EXAM_RESULTS, resultId));
}

export default {
  getAllExams,
  getExamById,
  getExamsByClass,
  getExamsByClasses,
  createExam,
  updateExam,
  deleteExam,
  subscribeToExamResults,
  getExamResults,
  getExamResultsByStudent,
  submitExamResult,
  gradeExamResult,
  deleteExamResult,
};
